import { ArrowLeftOutlined as ArrowLeft, ArrowLeftOutlined, ArrowRightOutlined, SwapOutlined, UndoOutlined } from '@ant-design/icons';
import { Alert, Button, Card, Descriptions, message, Popconfirm, Space, Spin, Tag, Typography } from "antd";
import dayjs from 'dayjs';
import React, { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import UserContext from "../../../../Contexts/UserContext";
import { RoutesLookup } from "../../../../Routes/RoutesLookup";
import AdminAppLayout from "../../../Layouts/AdminLayout";
import { colors } from "../../../utils/colors";
import { getRelatedTransactions, getTransferById, reverseTransaction, searchAdmins, searchAgents } from "./pointsTransferService";

const { Title } = Typography;

const TransferView = () => {
  const { auth } = useContext(UserContext);
  const [loading, setLoading] = useState(false);
  const [transfer, setTransfer] = useState(null);
  const [agents, setAgents] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [relatedTransactions, setRelatedTransactions] = useState(null);
  const [loadingRelated, setLoadingRelated] = useState(false);
  const navigate = useNavigate();
  const { id } = useParams();

  const loadTransfer = async () => {
    try {
      setLoading(true);
      const transferData = await getTransferById(parseInt(id));
      if (transferData) {
        setTransfer(transferData);
        
        // Load agents and admins for display names
        await Promise.all([
          loadAgents(),
          loadAdmins(),
          loadRelatedTransactions()
        ]);
      } else {
        message.error("Transfer not found");
        navigate(RoutesLookup.SETTINGS_POINTS_TRANSFER);
      }
    } catch (err) {
      message.error(err.message || "Failed to load transfer");
      navigate(RoutesLookup.SETTINGS_POINTS_TRANSFER);
    } finally {
      setLoading(false);
    }
  };

  const loadRelatedTransactions = async () => {
    try {
      setLoadingRelated(true);
      const related = await getRelatedTransactions(parseInt(id));
      setRelatedTransactions(related);
    } catch (err) {
      console.error("Failed to load related transactions:", err);
      setRelatedTransactions(null);
    } finally {
      setLoadingRelated(false);
    }
  };

  const loadAgents = async () => {
    try {
      const agentList = await searchAgents("");
      setAgents(Array.isArray(agentList) ? agentList : []);
    } catch (err) {
      console.error("Failed to load agents:", err);
    }
  };

  const loadAdmins = async () => {
    try {
      const adminList = await searchAdmins("");
      setAdmins(Array.isArray(adminList) ? adminList : []);
    } catch (err) {
      console.error("Failed to load admins:", err);
    }
  };

  useEffect(() => {
    if (auth?.token && id) {
      loadTransfer();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth?.token, id]);

  const getAgentName = (agentId) => {
    const agent = agents.find(a => a.KeyID === agentId);
    return agent ? `${agent.Name} (${agent.Email})` : 'N/A';
  };

  const getAdminName = (adminId) => {
    const admin = admins.find(a => a.KeyID === adminId);
    return admin ? `${admin.Name} (${admin.Email})` : 'N/A';
  };

  const handleBack = () => {
    navigate(RoutesLookup.SETTINGS_POINTS_TRANSFER);
  };

  const handleReverseTransaction = async () => {
    try {
      await reverseTransaction(parseInt(id));
      message.success("Transaction reversed successfully");
      navigate(RoutesLookup.SETTINGS_POINTS_TRANSFER);
    } catch (err) {
      message.error(err.message || "Failed to reverse transaction");
    }
  };

  const handleNavigateToRelated = (relatedTransferId) => {
    navigate(`/settings/points-transfer/view/${relatedTransferId}`);
  };

  if (loading) {
    return (
      <AdminAppLayout>
        <div
          style={{
            paddingLeft: 152,
            paddingRight: 152,
            marginTop: 40,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '400px'
          }}
        >
          <Spin size="large" />
        </div>
      </AdminAppLayout>
    );
  }

  if (!transfer) {
    return (
      <AdminAppLayout>
        <div
          className="page-content-wrapper"
          style={{
            textAlign: 'center',
          }}
        >
          <Title level={4}>Transfer not found</Title>
          <Button onClick={handleBack}>Back to Transfers</Button>
        </div>
      </AdminAppLayout>
    );
  }

  return (
    <AdminAppLayout>
      <div className="page-content-wrapper">
        <Title level={4} style={{ color: colors.black, marginBottom: 24 }}>
          Transfer Transaction Details
        </Title>
        
        <Space style={{ marginBottom: 24 }}>
          <Button 
            icon={<ArrowLeftOutlined />} 
            onClick={handleBack}
          >
            Back to Transfers
           </Button>
          {transfer && !transfer.RuleID && transfer.TransactionStatus !== "RVSD" && transfer.TransactionStatus !== "RVSN" && (
            <Popconfirm
              title="Reverse Transaction"
              description="Are you sure you want to reverse this transaction? This action cannot be undone."
              onConfirm={handleReverseTransaction}
              okText="Yes, Reverse"
              cancelText="Cancel"
            >
              <Button 
                type="primary"
                danger
                icon={<UndoOutlined />}
              >
                Reverse Transaction
              </Button>
            </Popconfirm>
          )}
        </Space>

        <Card>
          <Descriptions
            title="Transfer Information"
            bordered
            column={2}
            size="middle"
            labelStyle={{ 
              fontWeight: 'bold',
              backgroundColor: '#fafafa',
              width: '200px'
            }}
          >
            <Descriptions.Item label="Transfer Date" span={2}>
              <div style={{ fontSize: '16px' }}>
                {transfer.TransferDate 
                  ? dayjs(transfer.TransferDate).format('MMMM DD, YYYY [at] h:mm A')
                  : 'N/A'
                }
              </div>
            </Descriptions.Item>
            
            <Descriptions.Item label="Points Transferred" span={2}>
              <Tag color="blue" style={{ fontSize: '16px', padding: '6px 16px' }}>
                {transfer.Points || 0} Points
              </Tag>
            </Descriptions.Item>
            
            {(transfer.ReversedFlag === "R" || transfer.TransactionStatus === "RVSD" || transfer.TransactionStatus === "RVSN" || transfer.TransactionStatus === "RULE_REVERSE") && (
              <Descriptions.Item label="Type" span={2}>
                {transfer.ReversedFlag === "R" || transfer.TransactionStatus === "RVSD" ? (
                  <Tag color="orange" style={{ fontSize: '14px', padding: '4px 12px' }}>Reversed</Tag>
                ) : (
                  <Tag color="purple" style={{ fontSize: '14px', padding: '4px 12px' }}>Reverse Transaction</Tag>
                )}
              </Descriptions.Item>
            )}
            
            <Descriptions.Item label="From Agent" span={2}>
              <div style={{ fontSize: '16px' }}>
                {transfer.FromAgentName ? (
                  <div>
                    <strong>{transfer.FromAgentName}</strong>
                    {transfer.FromAgentEmail && (
                      <div style={{ color: '#666', fontSize: '14px' }}>
                        {transfer.FromAgentEmail}
                      </div>
                    )}
                  </div>
                ) : (
                  getAgentName(transfer.FromAgentID)
                )}
              </div>
            </Descriptions.Item>
            
            <Descriptions.Item label="To Agent" span={2}>
              <div style={{ fontSize: '16px' }}>
                {transfer.ToAgentName ? (
                  <div>
                    <strong>{transfer.ToAgentName}</strong>
                    {transfer.ToAgentEmail && (
                      <div style={{ color: '#666', fontSize: '14px' }}>
                        {transfer.ToAgentEmail}
                      </div>
                    )}
                  </div>
                ) : (
                  getAgentName(transfer.ToAgentID)
                )}
              </div>
            </Descriptions.Item>
            
            <Descriptions.Item label="Transferred By" span={2}>
              <div style={{ fontSize: '16px' }}>
                {transfer.TransferredByAdminName ? (
                  <div>
                    <strong>{transfer.TransferredByAdminName}</strong>
                    {transfer.TransferredByAdminEmail && (
                      <div style={{ color: '#666', fontSize: '14px' }}>
                        {transfer.TransferredByAdminEmail}
                      </div>
                    )}
                  </div>
                ) : (
                  getAdminName(transfer.TransferredByAdminID)
                )}
              </div>
            </Descriptions.Item>
            
            <Descriptions.Item label="Associated Rule" span={2}>
              <div style={{ fontSize: '16px' }}>
                {transfer.RuleID ? (
                  <div>
                    <div style={{ marginBottom: '8px' }}>
                      <Tag color="blue" style={{ fontSize: '14px', padding: '4px 12px' }}>
                        {transfer.RuleType || 'Unknown Rule Type'}
                      </Tag>
                    </div>
                    {transfer.RuleAgencyName && (
                      <div style={{ color: '#666', fontSize: '14px', marginBottom: '4px' }}>
                        <strong>Agency:</strong> {transfer.RuleAgencyName}
                      </div>
                    )}
                    {transfer.RuleAgentName && (
                      <div style={{ color: '#666', fontSize: '14px' }}>
                        <strong>Target Agent:</strong> {transfer.RuleAgentName}
                      </div>
                    )}
                    <div style={{ color: '#999', fontSize: '12px', marginTop: '8px' }}>
                      Rule ID: {transfer.RuleID}
                    </div>
                  </div>
                ) : (
                  <div style={{ color: '#999', fontStyle: 'italic' }}>
                    No associated rule - Manual transfer
                  </div>
                )}
              </div>
            </Descriptions.Item>
            
            <Descriptions.Item label="Associated Submission" span={2}>
              <div style={{ fontSize: '16px' }}>
                {transfer.SubmissionTransactionID ? (
                  <div>
                    <Tag color="green" style={{ fontSize: '14px', padding: '4px 12px' }}>
                      Submission Transaction
                    </Tag>
                    <div style={{ color: '#666', fontSize: '14px', marginTop: '8px' }}>
                      <strong>Submission ID:</strong> {transfer.SubmissionTransactionID}
                    </div>
                    <div style={{ color: '#999', fontSize: '12px', marginTop: '4px' }}>
                      This transfer was created from a specific submission transaction
                    </div>
                  </div>
                ) : (
                  <div style={{ color: '#999', fontSize: '14px' }}>
                    Manual transfer (not linked to a submission)
                  </div>
                )}
              </div>
            </Descriptions.Item>
            
            <Descriptions.Item label="Notes" span={2}>
              <div style={{ 
                fontSize: '16px',
                minHeight: '40px',
                padding: '8px',
                backgroundColor: '#f9f9f9',
                borderRadius: '4px',
                border: '1px solid #d9d9d9'
              }}>
                {transfer.Notes || 'No notes provided'}
              </div>
            </Descriptions.Item>
          </Descriptions>
        </Card>

        {/* Related Transactions Navigation */}
        {relatedTransactions && (relatedTransactions.originalTransaction || relatedTransactions.reversedTransaction) && (
          <Card 
            title={
              <Space>
                <SwapOutlined style={{ color: '#1890ff' }} />
                <span>Related Transactions</span>
              </Space>
            }
            style={{ marginTop: 24 }}
          >
            <Space direction="vertical" style={{ width: '100%' }} size="large">
              {/* Original Transaction */}
              {relatedTransactions.originalTransaction && (
                <Alert
                  message="Original Transaction"
                  description={
                    <div style={{ marginTop: 8 }}>
                      <div style={{ marginBottom: 8 }}>
                        <strong>Transfer #{relatedTransactions.originalTransaction.TransferID}</strong>
                        <Tag color="blue" style={{ marginLeft: 8 }}>
                          {relatedTransactions.originalTransaction.Points} points
                        </Tag>
                        <Tag color="orange" style={{ marginLeft: 4 }}>
                          {relatedTransactions.originalTransaction.ReversedFlag === "R" ? "Reversed" : "Original"}
                        </Tag>
                      </div>
                      <div style={{ color: '#666', fontSize: '14px', marginBottom: 8 }}>
                        {relatedTransactions.originalTransaction.FromAgentName} → {relatedTransactions.originalTransaction.ToAgentName}
                      </div>
                      <div style={{ color: '#999', fontSize: '12px' }}>
                        {dayjs(relatedTransactions.originalTransaction.TransferDate).format('MMM DD, YYYY [at] h:mm A')}
                      </div>
                    </div>
                  }
                  type="info"
                  action={
                    <Button 
                      type="primary" 
                      icon={<ArrowLeft />}
                      onClick={() => handleNavigateToRelated(relatedTransactions.originalTransaction.TransferID)}
                    >
                      View Original
                    </Button>
                  }
                  showIcon
                />
              )}

              {/* Reversed Transaction */}
              {relatedTransactions.reversedTransaction && (
                <Alert
                  message="Reversed Transaction"
                  description={
                    <div style={{ marginTop: 8 }}>
                      <div style={{ marginBottom: 8 }}>
                        <strong>Transfer #{relatedTransactions.reversedTransaction.TransferID}</strong>
                        <Tag color="green" style={{ marginLeft: 8 }}>
                          +{relatedTransactions.reversedTransaction.Points} points
                        </Tag>
                        <Tag color="purple" style={{ marginLeft: 4 }}>
                          Reverse Transaction
                        </Tag>
                      </div>
                      <div style={{ color: '#666', fontSize: '14px', marginBottom: 8 }}>
                        {relatedTransactions.reversedTransaction.FromAgentName} → {relatedTransactions.reversedTransaction.ToAgentName}
                      </div>
                      <div style={{ color: '#999', fontSize: '12px' }}>
                        {dayjs(relatedTransactions.reversedTransaction.TransferDate).format('MMM DD, YYYY [at] h:mm A')}
                      </div>
                    </div>
                  }
                  type="success"
                  action={
                    <Button 
                      type="primary" 
                      icon={<ArrowRightOutlined />}
                      onClick={() => handleNavigateToRelated(relatedTransactions.reversedTransaction.TransferID)}
                    >
                      View Reversal
                    </Button>
                  }
                  showIcon
                />
              )}

              {loadingRelated && (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                  <Spin size="small" /> Loading related transactions...
                </div>
              )}
            </Space>
          </Card>
        )}
      </div>
    </AdminAppLayout>
  );
};

export default TransferView;
